import React from 'react';
import {
  AgnesButton,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesDatePickerInput,
  AgnesIconButton,
  AgnesInput,
  AgnesInputFile,
  AgnesLabel,
  AgnesModal,
  AgnesPageHeader,
  AgnesPopover,
  AgnesPopoverContent,
  AgnesPopoverTrigger,
  AgnesRadioGroup,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesTimePickerInput,
  AgnesTooltip,
} from '@agnes/ui';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { ExportButton } from '@/components/ExportButton';
import {
  Download,
  FileText,
  Filter,
  History,
  Info,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { usePodeEditar } from '@/hooks/usePodeEditar';
import MENU_PERMISSIONS from '@/constants/ConstantMenuRoles';
import { useCertidaoFederalStore } from '@/stores/useCertidaoFederalStore';
import { confirm } from '@/stores/useConfirm';
import { useEmpregadoresStore } from '@/stores/useEmpregadoresStore';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { countFiltros, labelFiltro } from '@/lib/filtros';
import ServiceCertidaoFederal from '@/services/ServiceCertidaoFederal';
import { notify } from '@/stores/useToast';
import { SearchInput } from '@/components/SearchInput';
import UtilsMask from '@/utils/UtilsMask';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

// baixa o PDF da certidão via URL presigned (erro não estoura — notify)
async function baixarCertidao(key: string) {
  try {
    const { data } = await ServiceCertidaoFederal.downloadUrl(key);
    if (!data?.url) {
      notify.error('Documento indisponível.');
      return;
    }
    window.open(data.url, '_blank');
  } catch {
    notify.error('Não foi possível baixar o documento.');
  }
}

const SELECT_TRIGGER =
  '[&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

type Any = any;

interface Certidao {
  [k: string]: unknown;
  id: string;
  empregador: string;
  cnpj: string;
  tipo: string;
  emissao: string;
  validade: string;
  codigo: string;
  orientacao: string;
  arquivoCndId: string;
  arquivoPendenciaId: string;
}

const TIPOS = [
  { value: 'CND', label: 'Certidão Negativa' },
  { value: 'CPEN', label: 'Certidão Positiva com Efeitos de Negativa' },
];

// modal Adicionar certidão
function AdicionarCertidao({
  open,
  onClose,
  editar,
}: {
  open: boolean;
  onClose: () => void;
  editar?: Any;
}) {
  const empregadores = useEmpregadoresStore((s) => s.lista);
  const opcoesEmpregador = empregadores.map((e) => ({
    value: e.cnpjFilial,
    label: e.company ?? e.socialName ?? '',
  }));
  const [f, setF] = React.useState({
    empregador: '',
    tipo: 'CND',
    codigo: '',
    emissao: '',
    validade: '',
    hora: '',
    arquivoKey: '',
    arquivoPendenciaKey: '',
  });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const indisp = f.tipo === 'INDISPONIVEL';
  const podeSalvar =
    f.empregador !== '' &&
    (indisp || (f.codigo !== '' && f.emissao !== '' && f.validade !== '' && f.hora !== ''));

  React.useEffect(() => {
    if (!open) return;
    if (editar) {
      setF({
        empregador: editar.cnpj ?? '',
        tipo: editar._tipo ?? 'CND',
        codigo: editar.codigo ?? '',
        emissao: editar._emissao ?? '',
        validade: editar._validade ?? '',
        hora: editar._hora ?? '',
        arquivoKey: editar.arquivoCndId ?? '',
        arquivoPendenciaKey: editar.arquivoPendenciaId ?? '',
      });
    } else {
      setF({
        empregador: '',
        tipo: 'CND',
        codigo: '',
        emissao: '',
        validade: '',
        hora: '',
        arquivoKey: '',
        arquivoPendenciaKey: '',
      });
    }
  }, [editar, open]);

  return (
    <AgnesModal
      open={open}
      title={editar ? 'Editar certidão' : 'Adicionar certidão'}
      maxWidth="md"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3">
          <AgnesButton variant="text" onClick={onClose}>
            Cancelar
          </AgnesButton>
          <AgnesButton
            variant="primary"
            disabled={!podeSalvar}
            onClick={async () => {
              const dto = {
                cnpj: f.empregador,
                tipo: f.tipo,
                codigoControle: f.codigo || undefined,
                dataEmissao: f.emissao || undefined,
                dataValidade: f.validade || undefined,
                horaEmissao: f.hora || undefined,
                certidaoS3Id: f.arquivoKey || undefined,
                pendenciaS3Id: f.arquivoPendenciaKey || undefined,
              };
              if (editar?.id) await useCertidaoFederalStore.getState().atualizar(editar.id, dto);
              else await useCertidaoFederalStore.getState().salvar(dto);
              onClose();
            }}
          >
            Salvar
          </AgnesButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <AgnesLabel required>Empregador</AgnesLabel>
          {/* trocar o empregador limpa os arquivos já anexados: a key do S3 é derivada do CNPJ,
              então um arquivo enviado para outro empregador salvaria com o CNPJ errado. */}
          <AgnesSelect
            value={f.empregador}
            onValueChange={(v) =>
              setF((s) => ({ ...s, empregador: v, arquivoKey: '', arquivoPendenciaKey: '' }))
            }
          >
            <AgnesSelectTrigger className={SELECT_TRIGGER}>
              <AgnesSelectValue placeholder="Selecione uma opção" />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {opcoesEmpregador.map((o) => (
                <AgnesSelectItem key={o.value} value={o.value}>
                  {o.label}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
        </div>

        <AgnesRadioGroup
          label="Tipos *"
          orientation="horizontal"
          value={f.tipo}
          onValueChange={set('tipo')}
          options={[
            { value: 'CND', label: 'CND' },
            { value: 'CPEN', label: 'CPEN' },
            { value: 'INDISPONIVEL', label: 'Indisponível' },
          ]}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <AgnesInput
            label="Código de controle"
            hint="Preencha 16 caracteres (A-z, 0-9)"
            value={f.codigo}
            onChange={(e) => set('codigo')(e.target.value.toUpperCase())}
            placeholder="Obrigatório"
            disabled={indisp}
            maxLength={16}
          />
          <AgnesDatePickerInput
            label={indisp ? 'Data da tentativa' : 'Data da emissão'}
            hint="Preencha a data atual ou retroativa"
            value={f.emissao}
            onChange={(value) => set('emissao')(value)}
          />
          <AgnesDatePickerInput
            required
            label="Validade"
            hint="Preencha uma data futura"
            value={f.validade}
            onChange={(value) => set('validade')(value)}
            disabled={indisp}
          />
          <AgnesTimePickerInput
            label={indisp ? 'Hora da tentativa' : 'Hora da emissão'}
            hint="Preencha um horário"
            format="HH:MM"
            value={f.hora}
            onChange={(value) => set('hora')(value)}
          />
        </div>

        <div>
          <p className="font-semibold text-agnes-text">Envio de arquivos *</p>
          <p className="mb-2 text-sm text-agnes-text-muted">
            Adicione arquivos .pdf com no máximo 10 MB.
          </p>
          <div className="flex flex-col gap-3">
            <AgnesInputFile
              accept="application/pdf"
              disabled={indisp || !f.empregador}
              buttonLabel="Adicionar certidão *"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const cnpjLimpo = (f.empregador || '').replace(/[^a-zA-Z0-9]/g, '');
                const key = `${cnpjLimpo.slice(0, 8)}/certidoes/federal/manual_${cnpjLimpo}_${Date.now()}.pdf`;
                try {
                  await ServiceCertidaoFederal.uploadArquivo(file, key);
                  setF((s) => ({ ...s, arquivoKey: key }));
                } catch {
                  notify.error('Falha no upload do arquivo.');
                }
              }}
            />
            {f.arquivoKey && (
              <div
                className="flex items-center justify-between gap-2 rounded-lg border border-agnes-border px-3 py-2"
                data-qa="federal-arquivo-anexado"
              >
                <span className="truncate text-sm text-agnes-text">Documento anexado</span>
                <AgnesButton
                  variant="text"
                  size="sm"
                  startIcon={<Download className="h-4 w-4" />}
                  onClick={() => baixarCertidao(f.arquivoKey)}
                  aria-label="Baixar documento anexado"
                  data-qa="federal-arquivo-anexado-baixar"
                >
                  Baixar
                </AgnesButton>
              </div>
            )}
            <AgnesInputFile
              accept="application/pdf"
              disabled={indisp || !f.empregador}
              buttonLabel="Adicionar pendência"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const cnpjLimpo = (f.empregador || '').replace(/[^a-zA-Z0-9]/g, '');
                const key = `${cnpjLimpo.slice(0, 8)}/certidoes/federal/pendencia_${cnpjLimpo}_${Date.now()}.pdf`;
                try {
                  await ServiceCertidaoFederal.uploadArquivo(file, key);
                  setF((s) => ({ ...s, arquivoPendenciaKey: key }));
                } catch {
                  notify.error('Falha no upload do arquivo.');
                }
              }}
            />
            {f.arquivoPendenciaKey && (
              <div
                className="flex items-center justify-between gap-2 rounded-lg border border-agnes-border px-3 py-2"
                data-qa="federal-pendencia-anexada"
              >
                <span className="truncate text-sm text-agnes-text">Pendência anexada</span>
                <AgnesButton
                  variant="text"
                  size="sm"
                  startIcon={<Download className="h-4 w-4" />}
                  onClick={() => baixarCertidao(f.arquivoPendenciaKey)}
                  aria-label="Baixar pendência anexada"
                  data-qa="federal-pendencia-anexada-baixar"
                >
                  Baixar
                </AgnesButton>
              </div>
            )}
          </div>
        </div>

        <p className="text-sm font-semibold text-agnes-text">
          * Campos de preenchimento obrigatório
        </p>
      </div>
    </AgnesModal>
  );
}

// popover Filtro — aplica empregador + tipo server-side (o BFF só filtra por esses
// dois; range de data não é suportado pelo /search e foi removido do form).
function CertidaoFiltro() {
  const empregadores = useEmpregadoresStore((s) => s.lista);
  const opcoesEmpregador = empregadores.map((e) => ({
    value: e.cnpjFilial,
    label: e.company ?? e.socialName ?? '',
  }));
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState({ empregador: '', tipo: '' });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const filtroAplicado = useCertidaoFederalStore((s) => s.filtro);
  const nFiltros = countFiltros(filtroAplicado);
  const aplicar = () => {
    useCertidaoFederalStore.getState().aplicarFiltro(f);
    setOpen(false);
  };
  const limpar = () => {
    setF({ empregador: '', tipo: '' });
    useCertidaoFederalStore.getState().limparFiltro();
  };
  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" />}
          aria-label={nFiltros > 0 ? `Filtro, ${nFiltros} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', nFiltros)}
        </AgnesButton>
      </AgnesPopoverTrigger>
      <AgnesPopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-3 px-4 py-4">
          <AgnesSelect value={f.empregador} onValueChange={set('empregador')}>
            <AgnesSelectTrigger className={SELECT_TRIGGER}>
              <AgnesSelectValue placeholder="Empregador" />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {opcoesEmpregador.map((o) => (
                <AgnesSelectItem key={o.value} value={o.value}>
                  {o.label}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
          <AgnesSelect value={f.tipo} onValueChange={set('tipo')}>
            <AgnesSelectTrigger className={SELECT_TRIGGER}>
              <AgnesSelectValue placeholder="Tipo" />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {TIPOS.map((o) => (
                <AgnesSelectItem key={o.value} value={o.value}>
                  {o.label}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
        </div>
        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton variant="primary" onClick={aplicar} className="w-full">
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            startIcon={<Trash2 className="h-4 w-4" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}

const CNPJS_MONITORADOS_INFO =
  'As certidões listadas nesta tela são centralizadas no CNPJ matriz, logo, a certidão considera todos os CNPJs que possuam o mesmo CNPJ raiz. Deste modo a certidão abarca matriz e todas as filiais que compartilham o mesmo CNPJ raiz (8 primeiros dígitos do CNPJ são os mesmos).';

function StatCard({
  title,
  value,
  caption,
  accent,
}: {
  title: React.ReactNode;
  value: number;
  caption: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border border-agnes-border bg-agnes-bg-surface p-5 ${accent ? 'border-t-4 border-t-agnes-success' : ''}`}
    >
      <div className="flex items-start justify-between">
        <p className="text-agnes-text">{title}</p>
        {accent && (
          <AgnesTooltip className="max-w-sm whitespace-normal" content={CNPJS_MONITORADOS_INFO}>
            <Info className="h-4 w-4 shrink-0 text-agnes-primary" />
          </AgnesTooltip>
        )}
      </div>
      <p className="my-2 text-4xl font-bold text-agnes-text">{value}</p>
      <p className="text-sm text-agnes-text-muted">{caption}</p>
    </div>
  );
}

export default function PageCertidaoFederal() {
  const [modal, setModal] = React.useState(false);
  const [editarLinha, setEditarLinha] = React.useState<Any | null>(null);
  const [histId, setHistId] = React.useState<string | null>(null);
  const [syncCnpj, setSyncCnpj] = React.useState('');
  const podeEditar = usePodeEditar(MENU_PERMISSIONS.CERTIDAO_FEDERAL_EDITAR);

  const lista = useCertidaoFederalStore((s) => s.lista);
  const total = useCertidaoFederalStore((s) => s.total);
  const page = useCertidaoFederalStore((s) => s.page);
  const size = useCertidaoFederalStore((s) => s.size);
  const busca = useCertidaoFederalStore((s) => s.busca);
  const setBusca = useCertidaoFederalStore((s) => s.setBusca);
  const sortBy = useCertidaoFederalStore((s) => s.sortBy);
  const sortOrder = useCertidaoFederalStore((s) => s.sortOrder);
  const setSort = useCertidaoFederalStore((s) => s.setSort);
  const dashboard = useCertidaoFederalStore((s) => s.dashboard);
  const carregando = useCertidaoFederalStore((s) => s.carregando);
  const historico = useCertidaoFederalStore((s) => s.historico);
  const sincronizando = useCertidaoFederalStore((s) => s.sincronizando);
  const exportando = useCertidaoFederalStore((s) => s.exportando);
  const empregadores = useEmpregadoresStore((s) => s.lista);
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences('certidoes-federal');

  React.useEffect(() => {
    useEmpregadoresStore.getState().buscar();
    useCertidaoFederalStore.getState().carregar();
    useCertidaoFederalStore.getState().carregarDashboard();
  }, []);

  const totalMon = dashboard?.quantidadeTotalMonitoradas ?? 0;
  const pctMon = dashboard?.percentualTotalMonitoradas ?? 0;
  const suc = dashboard?.quantidadeSucesso ?? 0;
  const pctSuc = dashboard?.percentualSucesso ?? 0;
  const indisp = dashboard?.quantidadeIndisponivel ?? 0;
  const pctIndisp = dashboard?.percentualIndisponivel ?? 0;

  const columns: AgnesDataTableColumn<Certidao>[] = [
    {
      key: 'idx',
      header: '',
      width: 48,
      toggleable: false,
      render: (r) => (
        <span className="text-agnes-text-muted">{lista.indexOf(r) + 1 + page * size}</span>
      ),
    },
    { key: 'empregador', header: 'Empregador', width: 280, sortable: true },
    {
      key: 'cnpj',
      header: 'CNPJ',
      width: 170,
      sortable: true,
      render: (r) => UtilsMask.cnpj(r.cnpj),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      width: 200,
      sortable: true,
      render: (r) => (
        <span className="block max-w-[180px] truncate" title={r.tipo}>
          {r.tipo}
        </span>
      ),
    },
    { key: 'emissao', header: 'Emissão', width: 180, sortable: true },
    { key: 'validade', header: 'Validade', width: 130, sortable: true },
    { key: 'codigo', header: 'Código de controle', width: 180, sortable: true },
    {
      key: 'orientacoes',
      header: 'Orientações Agnes',
      width: 220,
      render: (r) =>
        r.orientacao ? (
          <span className="block max-w-[200px] truncate" title={r.orientacao}>
            {r.orientacao}
          </span>
        ) : (
          <span className="text-agnes-text-muted">-</span>
        ),
    },
    {
      key: 'acoes',
      header: 'Ações',
      width: 200,
      align: 'center',
      toggleable: false,
      render: (r) => (
        <div className="flex items-center justify-center gap-1">
          <AgnesIconButton
            aria-label="Relatório de situação fiscal"
            size="sm"
            disabled={!r.arquivoPendenciaId}
            onClick={() => baixarCertidao(r.arquivoPendenciaId)}
          >
            <FileText className="h-4 w-4" />
          </AgnesIconButton>
          <AgnesIconButton
            aria-label="Download da certidão"
            data-qa="federal-acao-download"
            size="sm"
            disabled={!r.arquivoCndId}
            onClick={() => baixarCertidao(r.arquivoCndId)}
          >
            <Download className="h-4 w-4" />
          </AgnesIconButton>
          <AgnesIconButton
            aria-label="Histórico"
            size="sm"
            onClick={() => {
              setHistId(r.id);
              useCertidaoFederalStore.getState().carregarHistorico(r.id);
            }}
          >
            <History className="h-4 w-4" />
          </AgnesIconButton>
          {podeEditar && (
            <>
              <AgnesIconButton
                aria-label="Editar"
                size="sm"
                onClick={() => {
                  setEditarLinha(r);
                  setModal(true);
                }}
              >
                <Pencil className="h-4 w-4" />
              </AgnesIconButton>
              <AgnesIconButton
                aria-label="Excluir"
                data-qa="federal-acao-excluir"
                size="sm"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Excluir certidão',
                    message: 'Deseja excluir esta certidão?',
                    okLabel: 'Excluir',
                    cancelLabel: 'Cancelar',
                    dataQa: 'federal-excluir-modal',
                  });
                  if (ok) useCertidaoFederalStore.getState().excluir(r.id);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </AgnesIconButton>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<FileText />}
        title="Certidão Federal"
        subtitle="Monitore a regularidade fiscal e previdenciária"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Certidões' },
          { label: 'Certidão Federal' },
        ]}
        actions={
          podeEditar && (
            <AgnesButton
              variant="primary"
              startIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setEditarLinha(null);
                setModal(true);
              }}
            >
              Adicionar
            </AgnesButton>
          )
        }
      />

      <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          accent
          title={
            <>
              Total de CNPJs <b>monitorados</b>
            </>
          }
          value={totalMon}
          caption={`${pctMon}% em relação ao total de CNPJs raíz cadastrados`}
        />
        <StatCard
          title={
            <>
              Certidões <b>emitidas com sucesso</b>
            </>
          }
          value={suc}
          caption={`${pctSuc}% em relação ao total de CNPJs monitorados`}
        />
        <StatCard
          title={
            <>
              Certidões <b>indisponíveis</b>
            </>
          }
          value={indisp}
          caption={`${pctIndisp}% em relação ao total de CNPJs monitorados`}
        />
      </div>

      <div className="mt-6 rounded-2xl border border-agnes-border bg-agnes-bg-surface p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-[320px] flex-1">
            <SearchInput
              value={busca}
              onValueChange={setBusca}
              onSearch={() => useCertidaoFederalStore.getState().buscar()}
              placeholder="Pesquisar por Empregador ou código de controle"
            />
          </div>
          {podeEditar && (
            <>
              <div className="min-w-[240px]">
                <AgnesSelect value={syncCnpj} onValueChange={setSyncCnpj}>
                  <AgnesSelectTrigger className={SELECT_TRIGGER}>
                    <AgnesSelectValue placeholder="Empregador para sincronizar" />
                  </AgnesSelectTrigger>
                  <AgnesSelectContent>
                    {empregadores.map((e: Any) => (
                      <AgnesSelectItem key={e.cnpjFilial} value={e.cnpjFilial}>
                        {e.company ?? e.socialName ?? ''}
                      </AgnesSelectItem>
                    ))}
                  </AgnesSelectContent>
                </AgnesSelect>
              </div>
              <AgnesButton
                variant="primary"
                disabled={!syncCnpj || sincronizando}
                startIcon={<RefreshCw className="h-4 w-4" />}
                onClick={() => useCertidaoFederalStore.getState().sincronizar(syncCnpj)}
              >
                {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
              </AgnesButton>
            </>
          )}
          <CertidaoFiltro />
          <ColumnsMenu
            data-qa="federal-colunas"
            columns={columns}
            hiddenColumnKeys={hiddenColumnKeys}
            onToggleColumn={toggleColumn}
          />
          <ExportButton
            variant="outlined"
            disabled={exportando}
            onClick={() => useCertidaoFederalStore.getState().exportar()}
          />
        </div>
        <TableWrapper className="mt-4">
          <AgnesDataTable<Certidao>
            columns={columns}
            hiddenColumnKeys={hiddenColumnKeys}
            data={lista}
            loading={carregando}
            rowKey={(r) => String(r.id)}
            sort={{ sortBy, sortOrder, onSortChange: setSort }}
            pagination={{
              page,
              pageSize: size,
              total,
              serverSide: true,
              onPageChange: (p: number) => useCertidaoFederalStore.getState().setPage(p),
              onPageSizeChange: (s: number) => useCertidaoFederalStore.getState().setSize(s),
            }}
          />
        </TableWrapper>
      </div>

      <AdicionarCertidao
        open={modal}
        onClose={() => {
          setModal(false);
          setEditarLinha(null);
        }}
        editar={editarLinha}
      />
      <AgnesModal
        open={!!histId}
        title="Histórico da certidão"
        maxWidth="lg"
        onClose={() => setHistId(null)}
      >
        <AgnesDataTable<Any>
          columns={[
            { key: 'tipo', header: 'Tipo' },
            { key: 'codigoControle', header: 'Código de controle' },
            { key: 'dataEmissao', header: 'Emissão' },
            { key: 'dataValidade', header: 'Validade' },
          ]}
          data={historico}
          rowKey={(r: Any) => String(r.id)}
        />
      </AgnesModal>
    </>
  );
}
